import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaCheck, FaFileAlt, FaSearch, FaTrash, FaUsers } from "react-icons/fa";
import {
  confirmClassroomTransitionDraft,
  updateClassroomTransitionDraft
} from "../services/TeacherAPIClient";
import {
  normaliseRosterName,
  sortClassroomTransitionChildren,
  toClassroomTransitionAllocation
} from "../utils/classroomTransitionRoster";
import {
  assertConfirmedMutationResponse,
  createWorkflowIdempotencyKey
} from "../utils/teacherWorkflowRequests";

const normaliseComparableText = (value) => normaliseRosterName(value).toLowerCase();
const getTeacherKey = (teacher) => [
  teacher?.kind,
  teacher?.teacherId || teacher?.pendingTeacherId || teacher?.email
].filter(Boolean).join(":");
const getTeacherIntent = (teacher) => ({
  kind: teacher.kind,
  teacherId: teacher.kind === "active" ? teacher.teacherId : undefined,
  pendingTeacherId: teacher.kind === "pending" ? teacher.pendingTeacherId : undefined,
  name: teacher.name,
  email: teacher.email
});

function TeacherClassroomReviewConfirm({
  review,
  teacherOptions,
  existingClasses,
  developmentMock = false,
  onBack,
  onConfirmed
}) {
  const [classroomName, setClassroomName] = useState(review.classroomName);
  const [teacherIntent, setTeacherIntent] = useState(review.teacherIntent);
  const [children, setChildren] = useState(() =>
    sortClassroomTransitionChildren(review.children)
  );
  const [draftVersion, setDraftVersion] = useState(review.version);
  const [search, setSearch] = useState("");
  const [savingRoster, setSavingRoster] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState("");
  const idempotencyKeyRef = useRef("");
  const submissionRef = useRef(false);

  const availableTeachers = useMemo(() => {
    const byKey = new Map();
    [review.teacherIntent, ...(teacherOptions || [])].forEach((teacher) => {
      const key = getTeacherKey(teacher);
      if (key) byKey.set(key, teacher);
    });
    return Array.from(byKey.values());
  }, [review.teacherIntent, teacherOptions]);
  const visibleChildren = useMemo(() => children.filter((child) =>
    normaliseComparableText(child.name).includes(normaliseComparableText(search))
  ), [children, search]);
  const trimmedClassroomName = normaliseRosterName(classroomName);
  const duplicateClassroomName = Boolean(trimmedClassroomName) && (existingClasses || []).some(
    (classItem) => {
      const academicYear = classItem?.academicYear || classItem?.academic_year ||
        classItem?.academicyear || classItem?.schoolYear || classItem?.yearLabel;
      return normaliseComparableText(classItem?.name || classItem?.className) ===
        normaliseComparableText(trimmedClassroomName) &&
        (!academicYear || academicYear === review.targetAcademicYear);
    }
  );
  const classroomNameError = !trimmedClassroomName
    ? "Enter a classroom name."
    : duplicateClassroomName
      ? `A class with this name already exists for ${review.targetAcademicYear}.`
      : "";
  const busy = savingRoster || submitting;
  const childLabel = children.length === 1 ? "child" : "children";

  const resetSubmissionKey = () => {
    idempotencyKeyRef.current = "";
    setError("");
    setStale(false);
  };

  const persistChildren = async (nextChildren) => {
    setSavingRoster(true);
    setError("");
    try {
      const response = developmentMock
        ? { id: review.draftId, version: Number(draftVersion) + 1 }
        : await updateClassroomTransitionDraft({
          schoolId: review.schoolId,
          targetAcademicYear: review.targetAcademicYear,
          draftId: review.draftId,
          progressionPointId: review.progressionPointId,
          classroomName: trimmedClassroomName,
          teacherIntent,
          method: review.method,
          version: draftVersion,
          allocations: nextChildren.map((child) =>
            toClassroomTransitionAllocation(child, review.method)
          ),
          unresolvedRows: [],
          sourceFilename: review.sourceFilename,
          sourceRowCount: review.sourceRowCount
        });
      if (!response?.id || response.version === undefined || response.version === null) {
        throw new Error("Invalid classroom draft response");
      }
      setChildren(nextChildren);
      setDraftVersion(response.version);
      resetSubmissionKey();
    } catch (saveError) {
      const isConflict = saveError?.response?.status === 409;
      setStale(isConflict);
      setError(isConflict
        ? "This classroom draft changed in another session. Go back and reopen it to load the latest roster."
        : "The child could not be removed. No changes were made. Please try again.");
    } finally {
      setSavingRoster(false);
    }
  };

  const confirmClassroom = async () => {
    if (submissionRef.current || classroomNameError || !teacherIntent || !children.length || stale) return;

    const idempotencyKey = idempotencyKeyRef.current || createWorkflowIdempotencyKey();
    idempotencyKeyRef.current = idempotencyKey;
    submissionRef.current = true;
    setSubmitting(true);
    setError("");
    let didConfirm = false;
    try {
      const response = developmentMock
        ? {
            status: "confirmed",
            auditReference: `development-${review.draftId}`,
            classroom: { id: `class-${review.draftId}`, name: trimmedClassroomName },
            progressionPoint: { confirmedCount: Number(review.confirmedCount || 0) + 1 }
          }
        : await confirmClassroomTransitionDraft({
          schoolId: review.schoolId,
          targetAcademicYear: review.targetAcademicYear,
          draftId: review.draftId,
          idempotencyKey,
          version: draftVersion,
          classroomName: trimmedClassroomName,
          teacherIntent
        });
      assertConfirmedMutationResponse(response, ["confirmed"]);
      const classroom = response?.classroom || response?.confirmedClassroom;
      if (!classroom?.id) throw new Error("Confirmation response did not include a classroom.");
      if (!Number.isFinite(Number(response?.progressionPoint?.confirmedCount))) {
        throw new Error("Confirmation response did not include committed progress.");
      }

      didConfirm = true;
      onConfirmed({
        response,
        review: {
          ...review,
          classroomName: trimmedClassroomName,
          teacherIntent,
          version: draftVersion,
          children
        }
      });
    } catch (confirmError) {
      const status = confirmError?.response?.status;
      const isConflict = status === 409;
      setStale(isConflict);
      if (isConflict) idempotencyKeyRef.current = "";
      setError(isConflict
        ? "This classroom draft changed or expired in another session. Go back and reopen it before confirming."
        : [400, 422].includes(status)
          ? "Check the classroom name, teacher and roster before trying again."
          : "The classroom was not confirmed. No changes were committed. Please try again.");
    } finally {
      submissionRef.current = false;
      if (!didConfirm) setSubmitting(false);
    }
  };

  const teacherStatus = teacherIntent?.kind === "active"
    ? "Active teacher"
    : teacherIntent?.kind === "pending"
      ? "Invitation pending"
      : "New teacher invitation pending";

  return (
    <section className="classroom-review" aria-labelledby="classroom-review-heading">
      <div className="classroom-review-heading">
        <span aria-hidden="true">2C</span>
        <div>
          <h3 id="classroom-review-heading">Review and confirm this classroom</h3>
          <p>Review the children, classroom name and teacher before confirming.</p>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="classroom-review-details">
        <label>
          <span>Classroom name</span>
          <input
            type="text"
            aria-label="Review classroom name"
            aria-invalid={Boolean(classroomNameError)}
            value={classroomName}
            onChange={(event) => {
              setClassroomName(event.target.value);
              resetSubmissionKey();
            }}
            disabled={busy}
          />
          {classroomNameError && <small role="alert">{classroomNameError}</small>}
        </label>
        <label>
          <span>Teacher</span>
          <select
            aria-label="Review classroom teacher"
            value={getTeacherKey(teacherIntent)}
            onChange={(event) => {
              const teacher = availableTeachers.find((item) => getTeacherKey(item) === event.target.value);
              if (teacher) setTeacherIntent(getTeacherIntent(teacher));
              resetSubmissionKey();
            }}
            disabled={busy}
          >
            {availableTeachers.map((teacher) => (
              <option value={getTeacherKey(teacher)} key={getTeacherKey(teacher)}>
                {teacher.name}
              </option>
            ))}
          </select>
          <small>{teacherStatus}</small>
        </label>
        <div className="classroom-review-source">
          <FaFileAlt aria-hidden="true" />
          <span>
            <small>Source</small>
            <strong>{review.method === "upload"
              ? `Uploaded from ${review.sourceFilename || "class list"}`
              : "Selected manually from last year’s children"}</strong>
          </span>
        </div>
      </div>

      <div className="classroom-review-roster-heading">
        <FaUsers aria-hidden="true" />
        <h3>Children in this classroom — {children.length}</h3>
      </div>
      <label className="classroom-population-search">
        <span className="visually-hidden">Search children in this classroom</span>
        <FaSearch aria-hidden="true" />
        <input
          type="search"
          placeholder="Search children in this classroom"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <ol className="classroom-review-roster" aria-label="Classroom review roster">
        {visibleChildren.map((child) => (
          <li key={`${child.kind}:${child.id || child.name}`}>
            <span>{child.name}{child.kind === "new" ? " (new)" : ""}</span>
            <button
              type="button"
              aria-label={`Remove ${child.name} from this classroom review`}
              title={`Remove ${child.name}`}
              onClick={() => persistChildren(children.filter((item) => item !== child))}
              disabled={busy || children.length === 1 || stale}
            >
              <FaTrash aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>

      <div className="classroom-review-footer">
        <Button variant="outline-primary" onClick={onBack} disabled={busy}>
          <FaArrowLeft aria-hidden="true" /> Back
        </Button>
        <Button
          variant="primary"
          className="classroom-review-confirm"
          onClick={confirmClassroom}
          disabled={busy || stale || Boolean(classroomNameError) || !teacherIntent || !children.length}
        >
          {submitting ? <><Spinner animation="border" size="sm" /> Confirming…</> : <>
            <FaCheck aria-hidden="true" /> Confirm {children.length} {childLabel} in this classroom
            <small>This classroom will be locked after confirmation.</small>
          </>}
        </Button>
      </div>
    </section>
  );
}

export default TeacherClassroomReviewConfirm;
